// Driver CRUD interface. Mirrors api.js — reads drivers/manifest.json
// in demo mode; future Supabase swap lands here without touching pages.

import { isConfigured } from './config.js';
import { loadDriver } from '../../shared/driver-schema.js';

const DRIVERS_DIR = globalThis.__RM_DRIVERS_BASE__ ?? '../drivers/';
const MANIFEST_PATH = `${DRIVERS_DIR}manifest.json`;
let manifestCache = null;

export async function listDrivers() {
  if (isConfigured()) throw new Error('Supabase client not implemented yet.');
  const manifest = await loadManifest();
  const summaries = await Promise.all(
    manifest.drivers.map(async filename => {
      try {
        const id = idFromFilename(filename);
        const data = await fetchDriverFile(filename);
        return summarize(id, data);
      } catch (err) {
        console.error(`Failed to load driver "${filename}":`, err);
        return null;
      }
    }),
  );
  return summaries.filter(Boolean);
}

export async function getDriver(id) {
  if (isConfigured()) throw new Error('Supabase client not implemented yet.');
  const manifest = await loadManifest();
  const filename = manifest.drivers.find(f => idFromFilename(f) === id);
  if (!filename) throw new Error(`Unknown driver: ${id}`);
  const data = await fetchDriverFile(filename);
  const { driver } = loadDriver(data);
  return driver;
}

async function loadManifest() {
  if (manifestCache) return manifestCache;
  // Cache-bust + no-store: without this, the browser's HTTP cache can
  // serve a stale manifest after files were renamed on disk, listing
  // filenames that 404 silently and leaving the list empty.
  const res = await fetch(`${MANIFEST_PATH}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Failed to load drivers manifest: ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data?.drivers)) throw new Error('Drivers manifest is malformed: missing "drivers" array');
  manifestCache = data;
  return data;
}

async function fetchDriverFile(filename) {
  const url = `${DRIVERS_DIR}${filename}?t=${Date.now()}`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function idFromFilename(filename) {
  return filename.replace(/\.driver\.json$/, '').replace(/\.json$/, '');
}

function summarize(id, data) {
  return {
    id,
    name: data.name || id,
    shortName: data.shortName || '',
    nationality: data.nationality || '',
    preferredNumber: typeof data.preferredNumber === 'number' ? data.preferredNumber : null,
    pace: data.attributes?.pace ?? 0.5,
  };
}
